import { useLanguage } from "@/context/LanguageContext";
import { Armchair, Store, TrendingUp, MapPin, Users, Package, ArrowRight } from "lucide-react";

export function FurnitureSection() {
  const { t } = useLanguage();

  const benefits = [
    { icon: Package, key: "catalog", color: "from-amber-500 to-orange-500" },
    { icon: Store, key: "dealers", color: "from-sky-500 to-cyan-500" },
    { icon: TrendingUp, key: "pricing", color: "from-emerald-500 to-green-500" },
    { icon: MapPin, key: "field", color: "from-violet-500 to-purple-500" },
    { icon: Users, key: "crm", color: "from-rose-500 to-pink-500" },
    { icon: Armchair, key: "b2b", color: "from-indigo-500 to-blue-500" },
  ];

  return (
    <section data-testid="furniture-section" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 mb-6">
            <Armchair className="w-4 h-4 text-amber-600" />
            <span className="text-xs sm:text-sm font-bold tracking-wide text-amber-700">
              {t("furniture.overline")}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
            {t("furniture.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-500">
            {t("furniture.subtitle")}
          </p>
        </div>

        {/* Hero showroom image - full width */}
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl mb-12">
          <img
            src="/images/furniture_hero.png"
            alt={t("imageAlt.furnitureShowroom")}
            width="1200"
            height="600"
            className="w-full h-64 sm:h-80 lg:h-96 object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Main content - 2 columns */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start mb-16">
          {/* Left - Benefits grid */}
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {benefits.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all group"
                    data-testid={`furniture-benefit-${item.key}`}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shrink-0 shadow-sm`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">{t(`furniture.${item.key}`)}</h4>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t(`furniture.${item.key}Desc`)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <a
              href="https://bayi.entegrepanel.net/register"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-[#22A7D1] to-[#32AED6] hover:shadow-lg hover:shadow-sky-500/20 transition-all hover:-translate-y-0.5"
              data-testid="furniture-cta"
            >
              {t("hero.cta")}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Right - Screenshots */}
          <div className="space-y-4">
            {/* Product catalog illustration */}
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-white p-2">
              <div className="rounded-xl overflow-hidden">
                <img
                  src="/images/furniture_catalog.png"
                  alt={t("imageAlt.furnitureCatalog")}
                  width="600"
                  height="400"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Dealer network */}
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-white p-2">
              <div className="rounded-xl overflow-hidden">
                <img
                  src="/images/furniture_dealer.png"
                  alt={t("imageAlt.furnitureDealer")}
                  width="600"
                  height="400"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Stats overlay */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 p-5 text-white text-center shadow-md">
                <div className="text-3xl font-black">313+</div>
                <div className="text-xs font-medium text-amber-100 mt-0.5">{t("furniture.quoteStat")}</div>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 p-5 text-white text-center shadow-md">
                <div className="text-3xl font-black">116+</div>
                <div className="text-xs font-medium text-sky-100 mt-0.5">{t("furniture.orderStat")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Management Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Grid Panel */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-all group">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Package className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">{t("furniture.catalogTitle")}</h3>
                  <p className="text-xs text-slate-500">{t("furniture.catalogSubtitle")}</p>
                </div>
              </div>
            </div>
            <div className="p-3">
              <div className="rounded-xl overflow-hidden">
                <img
                  src="/images/edited_offers.png"
                  alt={t("imageAlt.productManagement")}
                  width="600"
                  height="400"
                  className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          {/* Multi-Currency Price Lists */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-all group">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">{t("furniture.priceListTitle")}</h3>
                  <p className="text-xs text-slate-500">{t("furniture.priceListSubtitle")}</p>
                </div>
              </div>
            </div>
            <div className="p-3">
              <div className="rounded-xl overflow-hidden">
                <img
                  src="/images/multi_currency.png"
                  alt={t("imageAlt.multiCurrency")}
                  width="600"
                  height="400"
                  className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
